import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  LocalParkingFeeCalculationFull,
  LocalParkingFeeManualCalculatePayload,
} from '../../../../core/models/parking/local-parking-fee-calc.model';
import {
  ParkingGateOption,
  ParkingMainRateOption,
} from '../../../../core/models/system-settings/parking-master-data.model';
import { MapperLocalParkingFeeCalcService } from '../../../../core/mappers/parking/mapper-local-parking-fee-calc.service';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingFeeCalculationService } from '../../../../core/services/api/parking/api-parking-fee-calculation.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

export const CUSTOM_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  },
};

interface IdLabelOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-local-fee-calc-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssLoadingComponent,
  ],
  templateUrl: './local-fee-calc-test.component.html',
  styleUrl: './local-fee-calc-test.component.css',
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'th-TH' },
  ],
})
export class LocalFeeCalcTestComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingFeeCalculationService);
  private readonly masterApi = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperLocalParkingFeeCalcService);
  private readonly masterMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  isLoading = signal(false);
  isCalculating = signal(false);
  result = signal<LocalParkingFeeCalculationFull | null>(null);

  entryDate: Date | null = null;
  exitDate: Date | null = null;
  entryTime = '08:00';
  exitTime = '12:00';

  vehicleTypeId = 1;
  cardTypeId = 1;
  cardCode = '';
  entryGateId: number | null = null;
  stampRateId = '';
  defaultRateId = '';
  couponFreeMinutes = 0;
  paidParking = 0;
  paidPenalty = 0;
  isReprint = false;
  isHoliday = false;

  gateOptions = signal<ParkingGateOption[]>([]);
  rateOptions = signal<ParkingMainRateOption[]>([]);

  gateSearchText = '';
  stampRateSearchText = '';
  defaultRateSearchText = '';
  cardTypeSearchText = '';
  vehicleTypeSearchText = '';

  openDropdown: 'gate' | 'stampRate' | 'defaultRate' | 'cardType' | 'vehicleType' | null = null;

  readonly vehicleTypeOptions: IdLabelOption[] = [
    { id: 1, label: 'รถยนต์ (Car)' },
    { id: 2, label: 'รถจักรยานยนต์ (Motorcycle)' },
  ];

  readonly cardTypeOptions: IdLabelOption[] = [
    { id: 1, label: '1 — Guest / ทั่วไป' },
    { id: 2, label: '2 — Guest' },
    { id: 3, label: '3 — Member' },
    { id: 4, label: '4 — Member' },
  ];

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.result.set(null);
        this.entryGateId = null;
        this.gateSearchText = '';
        if (siteId) this.loadGateOptions(siteId);
        else this.gateOptions.set([]);
      });
    });
  }

  ngOnInit(): void {
    const now = new Date();
    const entry = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 0, 0);
    this.entryDate = entry;
    this.exitDate = now;
    this.entryTime = '08:00';
    this.exitTime = this.toTimeInput(now);
    this.loadRateOptions();
    this.syncLabelTexts();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get companyId(): number {
    const n = Number(this.cookieService.get('company_id') || 1);
    return n > 0 ? n : 1;
  }

  get filteredGates(): ParkingGateOption[] {
    const q = this.gateSearchText.trim().toLowerCase();
    const list = this.gateOptions();
    if (!q) return list;
    return list.filter(
      (g) => g.name.toLowerCase().includes(q) || String(g.id).includes(q),
    );
  }

  get filteredStampRates(): ParkingMainRateOption[] {
    return this.filterRates(this.stampRateSearchText);
  }

  get filteredDefaultRates(): ParkingMainRateOption[] {
    return this.filterRates(this.defaultRateSearchText);
  }

  get filteredCardTypes(): IdLabelOption[] {
    const q = this.cardTypeSearchText.trim().toLowerCase();
    if (!q) return this.cardTypeOptions;
    return this.cardTypeOptions.filter((o) => o.label.toLowerCase().includes(q));
  }

  get filteredVehicleTypes(): IdLabelOption[] {
    const q = this.vehicleTypeSearchText.trim().toLowerCase();
    if (!q) return this.vehicleTypeOptions;
    return this.vehicleTypeOptions.filter((o) => o.label.toLowerCase().includes(q));
  }

  // ================= Normal Functions =================

  syncLabelTexts(): void {
    const gate = this.gateOptions().find((g) => g.id === this.entryGateId);
    this.gateSearchText = gate ? `${gate.name} (#${gate.id})` : '';

    const stamp = this.rateOptions().find((r) => r.mainRateId === this.stampRateId);
    this.stampRateSearchText = stamp
      ? `${stamp.mainRateId}${stamp.name ? ' — ' + stamp.name : ''}`
      : this.stampRateId;

    const def = this.rateOptions().find((r) => r.mainRateId === this.defaultRateId);
    this.defaultRateSearchText = def
      ? `${def.mainRateId}${def.name ? ' — ' + def.name : ''}`
      : this.defaultRateId;

    const card = this.cardTypeOptions.find((c) => c.id === this.cardTypeId);
    this.cardTypeSearchText = card?.label ?? String(this.cardTypeId);

    const vehicle = this.vehicleTypeOptions.find((v) => v.id === this.vehicleTypeId);
    this.vehicleTypeSearchText = vehicle?.label ?? String(this.vehicleTypeId);
  }

  openDd(name: typeof this.openDropdown): void {
    this.openDropdown = name;
  }

  closeDropdowns(): void {
    this.openDropdown = null;
    this.revertUnmatchedTexts();
  }

  selectGate(gate: ParkingGateOption): void {
    this.entryGateId = gate.id;
    this.gateSearchText = `${gate.name} (#${gate.id})`;
    this.openDropdown = null;
  }

  selectStampRate(rate: ParkingMainRateOption): void {
    this.stampRateId = rate.mainRateId;
    this.stampRateSearchText = `${rate.mainRateId}${rate.name ? ' — ' + rate.name : ''}`;
    this.openDropdown = null;
  }

  clearStampRate(): void {
    this.stampRateId = '';
    this.stampRateSearchText = '';
  }

  selectDefaultRate(rate: ParkingMainRateOption): void {
    this.defaultRateId = rate.mainRateId;
    this.defaultRateSearchText = `${rate.mainRateId}${rate.name ? ' — ' + rate.name : ''}`;
    this.openDropdown = null;
  }

  clearDefaultRate(): void {
    this.defaultRateId = '';
    this.defaultRateSearchText = '';
  }

  selectCardType(opt: IdLabelOption): void {
    this.cardTypeId = opt.id;
    this.cardTypeSearchText = opt.label;
    this.openDropdown = null;
  }

  selectVehicleType(opt: IdLabelOption): void {
    this.vehicleTypeId = opt.id;
    this.vehicleTypeSearchText = opt.label;
    this.openDropdown = null;
  }

  resetForm(): void {
    this.ngOnInit();
    this.vehicleTypeId = 1;
    this.cardTypeId = 1;
    this.cardCode = '';
    this.entryGateId = null;
    this.stampRateId = '';
    this.defaultRateId = '';
    this.couponFreeMinutes = 0;
    this.paidParking = 0;
    this.paidPenalty = 0;
    this.isReprint = false;
    this.isHoliday = false;
    this.result.set(null);
    this.syncLabelTexts();
  }

  formatMoney(value: number): string {
    return (value ?? 0).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // ================= API Functions =================

  onCalculate(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.toast.error('กรุณาเลือกลานจาก Header');
      return;
    }
    if (!this.entryDate) {
      this.toast.error('กรุณาเลือกวันเวลาเข้า');
      return;
    }
    if (!this.exitDate) {
      this.toast.error('กรุณาเลือกวันเวลาออก');
      return;
    }

    const entry = this.combineDateTime(this.entryDate, this.entryTime);
    const exit = this.combineDateTime(this.exitDate, this.exitTime);
    if (!entry || !exit) {
      this.toast.error('วันเวลาไม่ถูกต้อง');
      return;
    }
    if (exit.getTime() < entry.getTime()) {
      this.toast.error('เวลาออกต้องไม่น้อยกว่าเวลาเข้า');
      return;
    }

    const payload: LocalParkingFeeManualCalculatePayload = {
      siteId,
      companyId: this.companyId,
      entryDate: this.toApiDateTime(entry),
      exitDate: this.toApiDateTime(exit),
      vehicleTypeId: this.vehicleTypeId,
      cardTypeId: this.cardTypeId,
      cardCode: this.cardCode.trim() || null,
      entryGateId: this.entryGateId ?? 0,
      stampRateId: this.stampRateId.trim() || null,
      defaultRateId: this.defaultRateId.trim() || null,
      couponFreeMinutes: Math.max(0, Number(this.couponFreeMinutes) || 0),
      paidParking: Math.max(0, Number(this.paidParking) || 0),
      paidPenalty: Math.max(0, Number(this.paidPenalty) || 0),
      isReprint: !!this.isReprint,
      isHoliday: !!this.isHoliday,
    };

    this.isCalculating.set(true);
    this.result.set(null);
    this.api.calculateManual(payload).subscribe({
      next: (res) => {
        console.log('calculateLocalParkingFeeManual', res);
        const mapped = this.mapper.mapCalculation(res);
        this.result.set(mapped);
        this.isCalculating.set(false);
        if (mapped.success) this.toast.success('คำนวณสำเร็จ');
        else this.toast.error(mapped.message || 'คำนวณไม่สำเร็จ');
      },
      error: (err) => {
        console.log('calculateLocalParkingFeeManual error', err);
        this.isCalculating.set(false);
        const body = err?.error;
        if (body) {
          const mapped = this.mapper.mapCalculation(body);
          this.result.set(mapped);
          this.toast.error(mapped.message || 'คำนวณไม่สำเร็จ');
        } else {
          this.toast.error(err?.message || 'เกิดข้อผิดพลาดระหว่างคำนวณ');
        }
      },
    });
  }

  private loadGateOptions(siteId: number): void {
    this.isLoading.set(true);
    this.masterApi.getParkingGatesForGroupOptions(siteId).subscribe({
      next: (res) => {
        console.log('getParkingGatesForGroupOptions', res);
        this.gateOptions.set(this.masterMapper.mapGateOptions(res));
        this.isLoading.set(false);
        this.syncLabelTexts();
      },
      error: (err) => {
        console.log('getParkingGatesForGroupOptions error', err);
        this.gateOptions.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดรายการประตูไม่สำเร็จ');
      },
    });
  }

  private loadRateOptions(): void {
    this.masterApi.getParkingMainRateOptions().subscribe({
      next: (res) => {
        console.log('getParkingMainRateOptions', res);
        this.rateOptions.set(this.masterMapper.mapMainRateOptions(res));
        this.syncLabelTexts();
      },
      error: (err) => {
        console.log('getParkingMainRateOptions error', err);
        this.rateOptions.set([]);
      },
    });
  }

  private filterRates(search: string): ParkingMainRateOption[] {
    const q = search.trim().toLowerCase();
    const list = this.rateOptions();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.mainRateId.toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q),
    );
  }

  private revertUnmatchedTexts(): void {
    const gate = this.gateOptions().find((g) => g.id === this.entryGateId);
    if (gate) this.gateSearchText = `${gate.name} (#${gate.id})`;
    else if (this.entryGateId) {
      /* keep */
    } else this.gateSearchText = '';

    const stamp = this.rateOptions().find((r) => r.mainRateId === this.stampRateId);
    this.stampRateSearchText = stamp
      ? `${stamp.mainRateId}${stamp.name ? ' — ' + stamp.name : ''}`
      : this.stampRateId;

    const def = this.rateOptions().find((r) => r.mainRateId === this.defaultRateId);
    this.defaultRateSearchText = def
      ? `${def.mainRateId}${def.name ? ' — ' + def.name : ''}`
      : this.defaultRateId;

    const card = this.cardTypeOptions.find((c) => c.id === this.cardTypeId);
    this.cardTypeSearchText = card?.label ?? String(this.cardTypeId);

    const vehicle = this.vehicleTypeOptions.find((v) => v.id === this.vehicleTypeId);
    this.vehicleTypeSearchText = vehicle?.label ?? String(this.vehicleTypeId);
  }

  private combineDateTime(date: Date, time: string): Date | null {
    const m = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
    if (!m) return null;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, 0, 0);
  }

  private toTimeInput(d: Date): string {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /** ส่งแบบ local datetime ไม่ใส่ Z เพื่อไม่เลื่อน timezone */
  private toApiDateTime(d: Date): string {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${mo}-${day}T${hh}:${mm}:${ss}`;
  }
}
